import { FilesetResolver, ImageSegmenter, ImageSegmenterResult } from "@mediapipe/tasks-vision";

// SPIKE (GPU-delegate segmentation): alternate mask source using MediaPipe Tasks Vision ImageSegmenter.
// Runs inference on the GPU delegate instead of CPU/WASM — the intended weak-device win (and it frees the
// main thread, which is why it may obviate the tflite worker). Exposes the SAME mask seam as the tflite
// path: a single-channel (person-probability) Float32Array consumed via getLatestMask().
//
// Behind a flag, default OFF (CONFIG.BG_SEGMENTATION_BACKEND). Falls back to tflite on init failure.
// Self-hosted assets (served from src/static, like the tflite models): tasks-vision wasm under
// /mediapipe/wasm and the selfie_segmenter model under /models. CONFIG can override both if needed.

export type SegmenterHandle = {
  // Run segmentation for this video frame (VIDEO mode is synchronous; updates the latest mask).
  segment(video: HTMLVideoElement, timestampMs: number): void;
  getLatestMask(): { data: Float32Array; offset: number } | null;
  // The selfie_segmenter square model outputs a 256x256 mask; the pipeline's segmentation texture must match.
  readonly maskWidth: number;
  readonly maskHeight: number;
  terminate(): void;
};

// Self-hosted (src/static/mediapipe/wasm and src/static/models). Override via CONFIG if paths differ.
const DEFAULT_WASM = "/mediapipe/wasm";
const DEFAULT_MODEL = "/models/selfie_segmenter.tflite";

export async function useImageSegmenter(): Promise<SegmenterHandle> {
  const cfg = (window as any).CONFIG ?? {};
  const wasmPath: string = cfg.BG_MEDIAPIPE_WASM ?? DEFAULT_WASM;
  const modelPath: string = cfg.BG_MEDIAPIPE_MODEL ?? DEFAULT_MODEL;

  const vision = await FilesetResolver.forVisionTasks(wasmPath);
  const segmenter = await ImageSegmenter.createFromOptions(vision, {
    baseOptions: { modelAssetPath: modelPath, delegate: "GPU" },
    runningMode: "VIDEO",
    outputCategoryMask: false,
    outputConfidenceMasks: true,
  });

  let latestMask: Float32Array | null = null;
  let maskWidth = 256;
  let maskHeight = 256;
  let logged = false;

  return {
    segment(video: HTMLVideoElement, timestampMs: number) {
      segmenter.segmentForVideo(video, timestampMs, (result: ImageSegmenterResult) => {
        const masks = result.confidenceMasks;
        if (masks && masks[0]) {
          maskWidth = masks[0].width;
          maskHeight = masks[0].height;
          const arr = masks[0].getAsFloat32Array();
          if (!logged) {
            // One-time diagnostic: actual mask dims (vs the pipeline's segmentation texture) + value range,
            // to pin down empty/misaligned masks and which confidence index is the person.
            logged = true;
            const stats = (a: Float32Array) => {
              let mn = Infinity,
                mx = -Infinity,
                sum = 0;
              for (let i = 0; i < a.length; i++) {
                const v = a[i];
                if (v < mn) mn = v;
                if (v > mx) mx = v;
                sum += v;
              }
              return `min=${mn.toFixed(3)} max=${mx.toFixed(3)} avg=${(sum / a.length).toFixed(3)}`;
            };
            console.log(
              `BgBlur MediaPipe: confidenceMasks=${masks.length} categoryMask=${!!result.categoryMask} ` +
                `maskSize=${maskWidth}x${maskHeight} arrLen=${arr.length} video=${video.videoWidth}x${video.videoHeight}`,
            );
            console.log(`BgBlur MediaPipe mask[0] ${stats(arr)}`);
            if (masks[1]) console.log(`BgBlur MediaPipe mask[1] ${stats(masks[1].getAsFloat32Array())}`);
          }
          // Copy before close(): the returned view is backed by mask memory freed by result.close().
          latestMask = arr.slice(0);
        }
        result.close();
      });
    },
    getLatestMask() {
      return latestMask ? { data: latestMask, offset: 0 } : null;
    },
    get maskWidth() {
      return maskWidth;
    },
    get maskHeight() {
      return maskHeight;
    },
    terminate() {
      segmenter.close();
    },
  };
}
